import React, { useState, useEffect } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import "./Dashboard.css";
import Sidebar from "../sidebar/Sidebar";
import Client from "../client/Client";
import SubVendor from "../subVendor/SubVendor";
import Recruitment from "../recruitment/Recruitment";
import Configuration from "../configuration/Configuration";
import Employee from "../employee/Employee";
import Attendance from "../Attendance/Attendance";
import Payroll from "../payroll/Payroll";
import Leaves from "../leaves/Leaves";
import Invoice from "../invoice/Invoice";
import Report from "../report/Report";
import Onboarding from "../recruitment/Onboarding/Onboarding";
import { HiOutlineUserGroup } from "react-icons/hi2";
import { BsCalendarCheck } from "react-icons/bs";
import { GoBriefcase, GoChecklist } from "react-icons/go";
import { FaMoneyBillWave } from "react-icons/fa";
import { FiAlertCircle } from "react-icons/fi";
import Greeting from "../greeting/Greeting";
import config from "../../config";
import Candidate from "../recruitment/Candidate/Candidate";
import { GrNotification } from "react-icons/gr";
import Header from "../../components/common/Header";
import Profile from "../profile/Profile";
import Separation from "../separation/Separation";

// --- Sub Components ---
const DashboardCard = ({ icon, title, value, date }) => (
  <div className="card">
    <div className="card-header">
      <span className="card-icon">{icon}</span>
      <span className="card-titlecard">{title}</span>
    </div>

    <div className="card-body">
      <div className="card-value">{value}</div>
    </div>

    <div className="card-footer">Update: {date}</div>
  </div>
);

const AttendanceRow = ({ name, designation, type, time, status }) => (
  <tr>
    <td className="dashboard-td">{name}</td>
    <td className="dashboard-td">{designation}</td>
    <td className="dashboard-td">{type}</td>
    <td className="dashboard-td">{time}</td>
    <td className="dashboard-td">
      <span className={status === "On Time" ? "status-on-time" : "status-late"}>
        {status}
      </span>
    </td>
  </tr>
);

// --- Main Dashboard Component ---
const Dashboard = () => {
  const [activePage, setActivePage] = useState(
    localStorage.getItem("userEmail") === "nvguhan@wtilth.com" ? "Clients" : "Dashboard"
  );
  const [activeDashboardTab, setActiveDashboardTab] = useState("Payroll");
  const [dashboardData, setDashboardData] = useState({});
  const [attendanceData, setAttendanceData] = useState([]);
  const [currentDate, setCurrentDate] = useState("");
  const [departments, setDepartments] = useState([]);
  const [clients, setClients] = useState([]);
  const [payrollFilters, setPayrollFilters] = useState({
    clientKey: "",
    month: "All",
    year: "2026",
    department: "All",
    status: "All",
  });
  const [payrollDashboardData, setPayrollDashboardData] = useState({
    totalPayroll: 0,
    employeesProcessed: 0,
    totalEmployees: 0,
    pendingPayrolls: 0,
    completionPercentage: 0,
    processingStatus: {
      paid: 0,
      unpaid: 0,
      total: 0,
      paidPercentage: 0
    },
    monthlyTrend: [],
    departmentWiseAnalysis: []
  });

  const [userDetails, setUserDetails] = useState({
    name: "",
    role: "",
    token: "",
    orgId: "",
  });

  // Effect to set formatted date
  useEffect(() => {
    const today = new Date();
    const options = { month: 'short', day: '2-digit', year: 'numeric' };
    const formattedDate = today.toLocaleDateString('en-US', options);
    setCurrentDate(formattedDate);
  }, []);


  useEffect(() => {
    const token = localStorage.getItem("Authorization") || "";
    const firstName = localStorage.getItem("firstName") || "";
    const lastName = localStorage.getItem("lastName") || "";
    const role = localStorage.getItem("role")?.toUpperCase() || "GUEST";
    const orgId = localStorage.getItem("orgId") || "";

    setUserDetails({
      name: `${firstName} ${lastName}`.trim(),
      role,
      token,
      orgId,
    });
  }, []);

  useEffect(() => {
    if (activePage !== "Dashboard" || !userDetails.token || !userDetails.orgId)
      return;

    const { token, orgId } = userDetails;
    const apiUrl = `${config.API_BASE_URL}${config.DASHBOARD_ENDPOINT}/${orgId}`;

    fetch(apiUrl, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch dashboard data");
        return res.json();
      })
      .then((data) => {
        if (data && data.body) {
          setDashboardData(data.body);
          setAttendanceData(data.body.attendance || []);
        }
      })
      .catch((err) => console.error("Fetch error:", err));
  }, [activePage, userDetails]);

  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;

    const fetchConfig = async (configType, setter) => {
      try {
        const response = await fetch(
          `${config.API_BASE_URL}${config.VIEW_CLIENT_CONFIG_ENDPOINT}${userDetails.orgId}?configType=${configType}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${userDetails.token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          const finalData = Array.isArray(data) ? data : (data.body || []);
          setter(finalData.filter((item) => item.isActive !== false));
        }
      } catch (err) {
        console.error(`Fetch ${configType} error:`, err);
      }
    };

    fetchConfig("Department", setDepartments);
  }, [userDetails.token, userDetails.orgId]);

  // Fetch client list for the Client dropdown
  useEffect(() => {
    if (!userDetails.token || !userDetails.orgId) return;
    const fetchClients = async () => {
      try {
        const response = await fetch(
          `${config.API_BASE_URL}${config.CLIENT_LIST_ENDPOINT}${userDetails.orgId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${userDetails.token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          const list = Array.isArray(data) ? data : (data.body || []);
          setClients(list);
          if (list.length > 0) {
            setPayrollFilters((prev) => ({
              ...prev,
              clientKey: list[0].clientKey,
            }));
          }
        }
      } catch (err) {
        console.error("Fetch clients error:", err);
      }
    };
    fetchClients();
  }, [userDetails.token, userDetails.orgId]);

  const fetchPayrollDashboard = async () => {
    if (!userDetails.token || !userDetails.orgId) return;

    try {
      // Params in exact order: orgKey → clientKey → month → year → departmentId → status
      const params = new URLSearchParams();
      params.set("orgKey", userDetails.orgId);
      if (payrollFilters.clientKey) params.set("clientKey", payrollFilters.clientKey);
      // Send ALL for month to get all months' data
      params.set("month", payrollFilters.month === "All" ? "ALL" : payrollFilters.month);
      if (payrollFilters.year) params.set("year", payrollFilters.year);
      if (payrollFilters.department !== "All" && payrollFilters.department !== "") {
        params.set("departmentId", payrollFilters.department);
      }
      if (payrollFilters.status !== "All") params.set("status", payrollFilters.status);

      const url = `${config.API_BASE_URL}${config.PAYROLL_DASHBOARD_VIEW_ENDPOINT}?${params.toString()}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${userDetails.token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Support both wrapped (data.body) and flat response shapes
        const body = data.body ?? data ?? {};
        const summary = body.summary ?? {};
        const processingStatus = body.processingStatus ?? {};

        // monthlyTrend: API returns array like [{month:"JAN", amount:210000}, ...]
        const rawTrend = body.payrollExpenseTrend ?? body.monthlyTrend ?? body.payrollTrend ?? body.trendData ?? [];
        const monthlyTrend = Array.isArray(rawTrend)
          ? rawTrend.map((item) => ({
            month: item.month ?? item.monthName ?? "",
            amount: item.amount ?? item.totalAmount ?? item.payrollAmount ?? 0,
          }))
          : [];

        const deptAnalysis = body.departmentWiseAnalysis ?? [];
        const departmentWiseAnalysis = Array.isArray(deptAnalysis)
          ? [...deptAnalysis].sort((a, b) => (b.totalPayroll ?? 0) - (a.totalPayroll ?? 0))
          : [];

        setPayrollDashboardData({
          totalPayroll: summary.totalPayrollAmount ?? 0,
          employeesProcessed: summary.totalEmployeesProcessed ?? 0,
          totalEmployees: processingStatus.total ?? 0,
          pendingPayrolls: summary.unpaidCount ?? 0,
          completionPercentage: processingStatus.paidPercentage ?? 0,
          processingStatus: {
            paid: processingStatus.paid ?? 0,
            unpaid: processingStatus.unpaid ?? 0,
            total: processingStatus.total ?? 0,
            paidPercentage: processingStatus.paidPercentage ?? 0,
          },
          monthlyTrend,
          departmentWiseAnalysis,
        });
      } else {
        console.error("Payroll dashboard API error:", response.status, response.statusText);
      }
    } catch (err) {
      console.error("Fetch payroll dashboard error:", err);
    }
  };

  // Auto-fetch on load and when filters change
  useEffect(() => {
    fetchPayrollDashboard();
  }, [userDetails.token, userDetails.orgId, payrollFilters]);

  const handleMenuClick = (name) => {
    setActivePage(name);
  };

  const renderActivePage = () => {
    switch (activePage) {
      case "Dashboard":
        return (
          <>
            <Header onProfileClick={() => setActivePage("Profile")}>
              <Greeting />
            </Header>

            {/* DASHBOARD CARDS */}
            {/* 
            <div className="dashboard-cards">
              ... OLD CARDS ...
            </div>
            */}

            <div className="dashboard-new-container">
              <h2 className="dashboard-title-new">Dashboard</h2>

              <div className="dashboard-tabs">
                <div
                  className={`dashboard-tab ${activeDashboardTab === "Overall" ? "active" : ""}`}
                  onClick={() => setActiveDashboardTab("Overall")}
                  style={{ cursor: "pointer" }}
                >
                  Overall
                </div>
                <div
                  className={`dashboard-tab ${activeDashboardTab === "Payroll" ? "active" : ""}`}
                  onClick={() => setActiveDashboardTab("Payroll")}
                  style={{ cursor: "pointer" }}
                >
                  Payroll
                </div>
              </div>

              {activeDashboardTab === "Overall" && (
                <div className="dashboard-cards" style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'flex-start' }}>
                  <DashboardCard
                    icon={<HiOutlineUserGroup />}
                    title="Total Employee"
                    value={dashboardData.totalEmployees || "0"}
                    date={currentDate}
                  />

                  <DashboardCard
                    icon={<BsCalendarCheck />}
                    title="Today Attendance"
                    value={dashboardData.lastMonthAttendance || "0"}
                    date={currentDate}
                  />

                  <DashboardCard
                    icon={<GoBriefcase />}
                    title="Total Applicant"
                    value={dashboardData.totalApplicants || "0"}
                    date={currentDate}
                  />

                  <DashboardCard
                    icon={<GoChecklist />}
                    title="Total Client"
                    value={dashboardData.totalClients || "0"}
                    date={currentDate}
                  />
                </div>
              )}

              {activeDashboardTab === "Payroll" && (
                <>
                  <div className="dashboard-filters">
                    <div className="filter-group">
                      <span className="filter-label">Client</span>
                      <select
                        className="filter-select"
                        value={payrollFilters.clientKey}
                        onChange={(e) => setPayrollFilters({ ...payrollFilters, clientKey: e.target.value })}
                      >
                        {clients.map((client) => (
                          <option key={client.clientKey} value={client.clientKey}>
                            {client.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-group">
                      <span className="filter-label">Month</span>
                      <select
                        className="filter-select"
                        value={payrollFilters.month}
                        onChange={(e) => setPayrollFilters({ ...payrollFilters, month: e.target.value })}
                      >
                        <option value="All">All</option>
                        <option value="JANUARY">January</option>
                        <option value="FEBRUARY">February</option>
                        <option value="MARCH">March</option>
                        <option value="APRIL">April</option>
                        <option value="MAY">May</option>
                        <option value="JUNE">June</option>
                        <option value="JULY">July</option>
                        <option value="AUGUST">August</option>
                        <option value="SEPTEMBER">September</option>
                        <option value="OCTOBER">October</option>
                        <option value="NOVEMBER">November</option>
                        <option value="DECEMBER">December</option>
                      </select>
                    </div>
                    <div className="filter-group">
                      <span className="filter-label">Department</span>
                      <select
                        className="filter-select"
                        value={payrollFilters.department}
                        onChange={(e) => setPayrollFilters({ ...payrollFilters, department: e.target.value })}
                      >
                        <option value="All">All</option>
                        {departments.map((dept) => (
                          <option key={dept.id} value={dept.id}>
                            {dept.values}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="filter-group">
                      <span className="filter-label">Status</span>
                      <select
                        className="filter-select"
                        value={payrollFilters.status}
                        onChange={(e) => setPayrollFilters({ ...payrollFilters, status: e.target.value })}
                      >
                        <option value="All">All</option>
                        <option value="PAID">PAID</option>
                        <option value="UNPAID">UNPAID</option>
                      </select>
                    </div>
                    <button className="filter-go-btn" onClick={fetchPayrollDashboard}>Go</button>
                  </div>

                  <div className="metric-cards-row">
                    {/* Card 1 */}
                    <div className="metric-card-new card-black">
                      <div className="metric-header">
                        <div className="metric-icon-box">
                          <FaMoneyBillWave style={{ fontSize: "16px" }} />
                        </div>
                        {/* Placeholder for percentage change if needed */}
                      </div>
                      <div className="metric-title">Total Payroll</div>
                      <div className="metric-value">₹{payrollDashboardData.totalPayroll.toLocaleString('en-IN')}</div>
                      <div className="metric-footer">Next disbursement scheduled: June 1st</div>
                    </div>

                    {/* Card 2 */}
                    {(() => {
                      const empPct = payrollDashboardData.totalEmployees > 0
                        ? Math.round((payrollDashboardData.employeesProcessed / payrollDashboardData.totalEmployees) * 100)
                        : 0;
                      return (
                        <div className="metric-card-new card-green">
                          <div className="metric-header">
                            <div className="metric-icon-box" style={{ color: "#10b981", background: "#ecfdf5" }}>
                              <HiOutlineUserGroup style={{ fontSize: "16px" }} />
                            </div>
                            <span className="metric-badge" style={{ color: "#374151" }}>{empPct}% completion</span>
                          </div>
                          <div className="metric-title">Employees Processed</div>
                          <div className="metric-value">{payrollDashboardData.employeesProcessed} / {payrollDashboardData.totalEmployees}</div>
                          <div className="progress-bar-bg" style={{ marginTop: "auto" }}>
                            <div className="progress-bar-fill" style={{ width: `${empPct}%`, transition: "width 0.6s ease" }}></div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Card 3 */}
                    <div className="metric-card-new card-yellow">
                      <div className="metric-header">
                        <div className="metric-icon-box" style={{ color: "#f59e0b", background: "#fffbeb" }}>
                          <GoChecklist style={{ fontSize: "16px" }} />
                        </div>
                        {payrollDashboardData.pendingPayrolls > 0 && (
                          <span className="metric-badge badge-yellow">⚠ URGENT FLAG</span>
                        )}
                      </div>
                      <div className="metric-title">Pending Payrolls</div>
                      <div className="metric-value">{payrollDashboardData.pendingPayrolls}</div>
                      <div className="metric-footer">Awaiting final admin approval</div>
                    </div>

                    {/* Card 4 */}
                    <div className="metric-card-new card-red">
                      <div className="metric-header">
                        <div className="metric-icon-box" style={{ color: "#ef4444", background: "#fef2f2" }}>
                          <FiAlertCircle style={{ fontSize: "16px" }} />
                        </div>
                        <span className="metric-badge badge-red">HIGH PRIORITY</span>
                      </div>
                      <div className="metric-title">Failed Transactions</div>
                      <div className="metric-value" style={{ color: "#ef4444" }}>0</div>
                      <div className="metric-footer">Re-processing recommended</div>
                    </div>
                  </div>

                  <div className="charts-row">
                    {/* Line Chart — renders only when API trend data exists */}
                    <div className="chart-card">
                      <div className="chart-header">
                        <div className="chart-title">Payroll Expense Trend</div>
                        {payrollDashboardData.monthlyTrend.length > 0 && (
                          <div style={{ fontSize: "12px", color: "#4b5563", display: "flex", alignItems: "center", gap: "6px" }}>
                            <span style={{ width: "8px", height: "8px", background: "#13418bff", borderRadius: "50%", display: "inline-block" }}></span>
                            Monthly Expense
                          </div>
                        )}
                      </div>

                      {payrollDashboardData.monthlyTrend.length > 0 ? (() => {
                        const trend = payrollDashboardData.monthlyTrend;
                        const amounts = trend.map((d) => d.amount);
                        const minAmt = Math.min(...amounts);
                        const maxAmt = Math.max(...amounts);
                        const range = maxAmt - minAmt || 1;
                        const chartW = 500;
                        const chartH = 150;
                        const padX = 20;
                        const padY = 20;
                        const usableW = chartW - padX * 2;
                        const usableH = chartH - padY * 2;
                        
                        const coords = trend.map((d, i) => {
                          const x = trend.length === 1 
                            ? chartW / 2 
                            : padX + (i / (trend.length - 1 || 1)) * usableW;
                          const y = maxAmt === minAmt
                            ? padY + usableH / 2
                            : padY + usableH - ((d.amount - minAmt) / range) * usableH;
                          return { x, y, month: d.month, amount: d.amount };
                        });
                        
                        const points = coords.map(c => `${c.x},${c.y}`).join(" ");
                        const fmt = (v) => v >= 1_000_000 ? `₹${(v / 1_000_000).toFixed(2)}M` : v >= 1_000 ? `₹${(v / 1_000).toFixed(1)}K` : `₹${v}`;
                        return (
                          <>
                            <div className="chart-subtitle">
                              {trend[0]?.month} {trend.length > 1 ? `— ${trend[trend.length - 1]?.month}` : ""}
                            </div>
                            <div style={{ height: "200px", position: "relative", borderBottom: "1px solid #e5e7eb" }}>
                              <svg viewBox={`0 0 ${chartW} ${chartH}`} style={{ width: "100%", height: "100%", overflow: "visible", preserveAspectRatio: "none" }}>
                                <defs>
                                  <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#0e449c" stopOpacity="0.2"/>
                                    <stop offset="100%" stopColor="#0e449c" stopOpacity="0.0"/>
                                  </linearGradient>
                                </defs>
                                
                                {coords.length > 1 && (
                                  <>
                                    <polygon
                                      fill="url(#chartGradient)"
                                      points={`${coords[0].x},${padY + usableH} ${points} ${coords[coords.length - 1].x},${padY + usableH}`}
                                    />
                                    <polyline
                                      fill="none"
                                      stroke="#0e449cff"
                                      strokeWidth="3.5"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      points={points}
                                    />
                                  </>
                                )}
                                
                                {coords.map((c, i) => (
                                  <g key={i}>
                                    <circle
                                      cx={c.x}
                                      cy={c.y}
                                      r="5"
                                      fill="#0e449cff"
                                      stroke="#ffffff"
                                      strokeWidth="2"
                                    />
                                    <title>{c.month}: {fmt(c.amount)}</title>
                                  </g>
                                ))}
                              </svg>
                            </div>
                            <div style={{ display: "flex", justifyContent: trend.length === 1 ? "center" : "space-between", marginTop: "12px", fontSize: "10px", color: "#6b7280", fontWeight: "600", padding: "0 10px" }}>
                              {trend.map((d, i) => <span key={i}>{d.month}</span>)}
                            </div>
                            <div style={{ textAlign: "right", fontSize: "11px", color: "#6b7280", marginTop: "16px" }}>
                              ↑ Highest: {fmt(maxAmt)} &nbsp;&nbsp; ↓ Lowest: {fmt(minAmt)}
                            </div>
                          </>
                        );
                      })() : (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "220px", color: "#9ca3af" }}>
                          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: "12px" }}>
                            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                          </svg>
                          <span style={{ fontSize: "13px", fontWeight: "500" }}>No trend data available</span>
                          <span style={{ fontSize: "11px", marginTop: "4px" }}>Apply filters and click Go to load data</span>
                        </div>
                      )}
                    </div>

                    {/* Processing Status Donut Chart */}
                    <div className="chart-card">
                      <div className="chart-title">Processing Status</div>
                      <div className="chart-subtitle">
                        {payrollFilters.month === "All" ? "All Time" : payrollFilters.month} {payrollFilters.year} • {payrollDashboardData.processingStatus.total} Payrolls
                      </div>

                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                        <div style={{ position: "relative", width: "200px", height: "200px" }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              {/* Inner Ring (Lighter) */}
                              <Pie
                                data={[
                                  { name: "Paid", value: payrollDashboardData.processingStatus.paid, color: "#64b5f6" },
                                  { name: "Pending", value: payrollDashboardData.processingStatus.unpaid, color: "#fcd34d" },
                                  { name: "Failed", value: 0, color: "#fca5a5" },
                                  { name: "On Hold", value: 0, color: "#9ca3af" }
                                ]}
                                dataKey="value"
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={65}
                                paddingAngle={3}
                                stroke="none"
                              >
                                {[
                                  { color: "#6cb8e6" },
                                  { color: "#f8c564" },
                                  { color: "#f57b7c" },
                                  { color: "#9ca3af" }
                                ].map((entry, index) => (
                                  <Cell key={`cell-inner-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              {/* Outer Ring (Darker) */}
                              <Pie
                                data={[
                                  { name: "Paid", value: payrollDashboardData.processingStatus.paid, color: "#2196f3" },
                                  { name: "Pending", value: payrollDashboardData.processingStatus.unpaid, color: "#f59e0b" },
                                  { name: "Failed", value: 0, color: "#ef4444" },
                                  { name: "On Hold", value: 0, color: "#6b7280" }
                                ]}
                                dataKey="value"
                                cx="50%"
                                cy="50%"
                                innerRadius={65}
                                outerRadius={85}
                                paddingAngle={3}
                                stroke="none"
                              >
                                {[
                                  { color: "#40bce9ff" },
                                  { color: "#f1a415" },
                                  { color: "#e04848" },
                                  { color: "#6b7280" }
                                ].map((entry, index) => (
                                  <Cell key={`cell-outer-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                            </PieChart>
                          </ResponsiveContainer>

                          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <span style={{ fontSize: "24px", fontWeight: "700", color: "#1f2937" }}>{payrollDashboardData.processingStatus.paidPercentage}%</span>
                            <span style={{ fontSize: "10px", fontWeight: "700", color: "#4b5563" }}>PAID</span>
                          </div>
                        </div>

                        <div style={{ width: "100%", marginTop: "24px", padding: "0 10px" }}>
                          {[
                            { name: "Paid", value: payrollDashboardData.processingStatus.paid, color: "#2596be" },
                            { name: "Pending", value: payrollDashboardData.processingStatus.unpaid, color: "#f1a415" },
                            { name: "Failed", value: 0, color: "#e04848" },
                            { name: "On Hold", value: 0, color: "#6b7280" }
                          ].map((item, index) => (
                            <div key={index} style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", fontSize: "12px", color: "#4b5563" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <span style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: item.color }}></span>
                                {item.name}
                              </div>
                              <span style={{ fontWeight: "700", color: "#111827" }}>{item.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Department Analysis Chart */}
                  <div className="department-analysis-card">
                    <div className="department-analysis-title">Department-wise Analysis</div>
                    <div className="department-analysis-subtitle">Sorted by Total Cost (Highest → Lowest)</div>

                    {payrollDashboardData.departmentWiseAnalysis.length > 0 ? (() => {
                      const totalPayrollSum = payrollDashboardData.departmentWiseAnalysis.reduce(
                        (sum, d) => sum + (d.totalPayroll ?? 0),
                        0
                      );
                      const colors = ["#1e3a8a", "#2563eb", "#3b82f6", "#60a5fa", "#93c5fd"];
                      return payrollDashboardData.departmentWiseAnalysis.map((item, index) => {
                        const pct = totalPayrollSum > 0
                          ? Math.round(((item.totalPayroll ?? 0) / totalPayrollSum) * 100)
                          : 0;
                        const barColor = colors[index % colors.length];
                        return (
                          <div className="dept-bar-row" key={item.department ?? index}>
                            <div className="dept-name">{item.department || "Unknown"}</div>
                            <div className="dept-bar-track">
                              <div
                                className="dept-bar-fill"
                                style={{
                                  width: `${pct > 0 ? pct : 5}%`,
                                  backgroundColor: barColor,
                                }}
                              >
                                ₹{(item.totalPayroll ?? 0).toLocaleString('en-IN')}
                              </div>
                            </div>
                            <div className="dept-pct">{pct}% of Total</div>
                          </div>
                        );
                      });
                    })() : (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "150px", color: "#9ca3af" }}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: "12px" }}>
                          <line x1="18" y1="20" x2="18" y2="10" />
                          <line x1="12" y1="20" x2="12" y2="4" />
                          <line x1="6" y1="20" x2="6" y2="14" />
                        </svg>
                        <span style={{ fontSize: "13px", fontWeight: "500" }}>No department-wise data available</span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* ATTENDANCE TABLE */}
            {/* <div className="dashboard-table-container">
              <h3>Attendance Overview</h3>

              <table className="dashboard-table">
                <thead>
                  <tr>
                    <th className="dashboard-th">Employee Name</th>
                    <th className="dashboard-th">Designation</th>
                    <th className="dashboard-th">Type</th>
                    <th className="dashboard-th">Check In Time</th>
                    <th className="dashboard-th">Status</th>
                  </tr>
                </thead>

                <tbody>
                  {attendanceData.map((data, index) => (
                    <AttendanceRow
                      key={index}
                      name={data.name}
                      designation={data.designation}
                      type={data.type}
                      time={data.time}
                      status={data.status}
                    />
                  ))}
                </tbody>
              </table>
            </div> */}
          </>
        );

      case "Clients": return <Client setActivePage={handleMenuClick} />;
      case "Sub Vendors": return <SubVendor setActivePage={handleMenuClick} />;
      case "Recruitment": return <Recruitment setActivePage={handleMenuClick} />;
      case "Configuration": return <Configuration setActivePage={handleMenuClick} />;
      case "Employee": return <Employee setActivePage={handleMenuClick} />;
      case "Separation": return <Separation setActivePage={handleMenuClick} />;
      case "Attendance": return <Attendance setActivePage={handleMenuClick} />;
      case "Payroll": return <Payroll setActivePage={handleMenuClick} />;
      case "Leaves": return <Leaves setActivePage={handleMenuClick} />;
      case "Invoice": return <Invoice setActivePage={handleMenuClick} />;
      case "Report":
      case "Reports": return <Report setActivePage={handleMenuClick} />;
      case "Candidate": return <Candidate setActivePage={handleMenuClick} />;
      case "Onboarding": return <Onboarding setActivePage={handleMenuClick} />;
      // Also keep some alternates just in case
      case "Recruitment List": return <Recruitment setActivePage={handleMenuClick} />;
      case "Candidate List": return <Candidate setActivePage={handleMenuClick} />;
      case "Onboarding List": return <Onboarding setActivePage={handleMenuClick} />;
      case "Profile": return <Profile setActivePage={handleMenuClick} />;
      default: return <p>Page Not Found</p>;
    }
  };

  return (
    <div className="container">
      <Sidebar activePage={activePage} onMenuClick={handleMenuClick} />
      <div className="content">{renderActivePage()}</div>
    </div>
  );
};

export default Dashboard;